import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, LogIn } from "lucide-react";
import { BRAND } from "@/lib/content";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/programs", label: "Programs" },
  { to: "/blog", label: "Blog" },
  { to: "/booking", label: "Booking" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-md bg-[#F6EFE9]/90 border-b border-[#F3C9D3]/40"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="font-serif-display text-2xl text-[#3A3A3A]"
          data-testid="brand-logo"
          onClick={() => setOpen(false)}
        >
          <span className="text-[#E8A6B8]">Her</span>FitHub
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.18em] font-medium transition-colors ${
                  isActive
                    ? "text-[#E8A6B8]"
                    : "text-[#3A3A3A] hover:text-[#E8A6B8]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <a
            href={BRAND.clientAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-semibold text-[#3A3A3A]/80 hover:text-[#E8A6B8] transition-colors"
            data-testid="nav-client-login"
            aria-label={`Client login — ${BRAND.clientAppName} app`}
            title={`${BRAND.clientAppName} · client coaching app`}
          >
            <LogIn size={13} strokeWidth={1.8} />
            Client Login
          </a>
          <Link
            to="/booking"
            className="btn-pink"
            data-testid="nav-book-consultation"
          >
            Book a Consultation
          </Link>
        </div>

        <button
          className="md:hidden p-2 text-[#3A3A3A]"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden bg-[#F3C9D3]/95 border-t border-[#E8A6B8]/30 px-6 py-6 space-y-4"
          data-testid="mobile-menu"
        >
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block text-sm uppercase tracking-[0.18em] font-medium ${
                  isActive ? "text-white" : "text-[#3A3A3A]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/booking"
            onClick={() => setOpen(false)}
            className="btn-pink w-full"
            data-testid="mobile-nav-book"
          >
            Book a Consultation
          </Link>
          <a
            href={BRAND.clientAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-2 w-full pt-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3A3A3A] border-t border-[#E8A6B8]/30"
            data-testid="mobile-nav-client-login"
            aria-label={`Client login — ${BRAND.clientAppName} app`}
          >
            <LogIn size={13} strokeWidth={1.8} />
            Client Login · {BRAND.clientAppName}
          </a>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer
      className="bg-[#3A3A3A] text-[#F6EFE9] mt-24"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="font-serif-display text-2xl mb-3">
            <span className="text-[#E8A6B8]">Her</span>FitHub
          </div>
          <p className="text-sm text-white/70 leading-relaxed max-w-xs">
            Women's strength coaching with {BRAND.coachName}. Realistic
            training. Real strength. Real life.
          </p>
        </div>
        <div>
          <p className="eyebrow text-white/80 mb-4">Explore</p>
          <ul className="space-y-2 text-sm text-white/80">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-[#E8A6B8] transition">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="eyebrow text-white/80 mb-4">Existing Clients</p>
          <p className="text-xs text-white/60 leading-relaxed mb-3">
            Active clients receive their training plan, check-ins and progress
            tracking through the <strong className="text-white/80">{BRAND.clientAppName}</strong>{" "}
            coaching app.
          </p>
          <a
            href={BRAND.clientAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#E8A6B8] hover:text-white transition-colors"
            data-testid="footer-client-login"
          >
            Client Login →
          </a>
        </div>
        <div>
          <p className="eyebrow text-white/80 mb-4">Connect</p>
          <ul className="space-y-2 text-sm text-white/80">
            <li>
              <a
                href={`mailto:${BRAND.email}`}
                className="hover:text-[#E8A6B8] transition"
                data-testid="footer-email"
              >
                {BRAND.email}
              </a>
            </li>
            {BRAND.instagram.map((ig) => (
              <li key={ig.handle}>
                <a
                  href={ig.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 hover:text-[#E8A6B8] transition"
                  data-testid={`footer-ig-${ig.handle}`}
                >
                  <Instagram size={14} /> {ig.handle}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-xs text-white/50 flex flex-col md:flex-row justify-between gap-2">
          <p>© {new Date().getFullYear()} HerFitHub. All rights reserved.</p>
          <p>Designed with strength & softness.</p>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#F6EFE9]">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
